import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Check, Leaf } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Guest cart helper functions
const getGuestCart = () => JSON.parse(localStorage.getItem('guestCart') || '[]');
const saveGuestCart = (cart) => localStorage.setItem('guestCart', JSON.stringify(cart));

export default function TeaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [tea, setTea] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTea = async () => {
      try {
        const response = await axios.get(`${API}/teas/${id}`);
        setTea(response.data);
      } catch (error) {
        console.error('Error fetching tea:', error);
        toast.error('Tea not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchTea();
  }, [id, navigate]);

  const addToCart = () => {
    if (user) {
      // Logged in user - use API
      axios.post(`${API}/cart`, { item_id: tea.id, item_type: 'tea', quantity: 1 }, { withCredentials: true })
        .then(() => {
          fetchCartCount();
          toast.success('Added to cart!');
        })
        .catch(() => {
          toast.error('Failed to add to cart');
        });
    } else {
      // Guest user - use localStorage
      const guestCart = getGuestCart();
      const existingItem = guestCart.find(item => item.cart_item.item_id === tea.id);
      
      if (existingItem) {
        existingItem.cart_item.quantity += 1;
      } else {
        const timestamp = new Date().getTime();
        guestCart.push({
          cart_item: {
            id: `guest_${timestamp}`,
            item_id: tea.id,
            item_type: 'tea',
            quantity: 1
          },
          item_details: {
            id: tea.id,
            name: tea.name,
            price: tea.price,
            image_url: tea.image_url,
            category: tea.category
          }
        });
      }
      
      saveGuestCart(guestCart);
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('guestCartUpdated'));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.85), rgba(204, 85, 0, 0.85)), url(https://images.pexels.com/photos/34990728/pexels-photo-34990728.jpeg?auto=compress&cs=tinysrgb&w=1920)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button variant="ghost" onClick={() => navigate('/shop')} className="mb-6 text-white hover:bg-white/10" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Shop
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img src={tea.image_url} alt={tea.name} className="w-full h-full object-cover" />
          </div>

          <div className="space-y-6 bg-white/95 backdrop-blur-sm p-8 rounded-2xl">
            <div>
              <span className="inline-block bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm mb-4">{tea.category}</span>
              <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="tea-name">
                {tea.name}
              </h1>
              <p className="text-2xl font-bold text-orange-600 mb-6" data-testid="tea-price">${tea.price.toFixed(2)}</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Description</h2>
              <p className="text-gray-700 leading-relaxed">{tea.description}</p>
            </div>

            {/* Benefits Section */}
            {tea.benefits && tea.benefits.length > 0 && (
              <div className="bg-emerald-50 p-4 rounded-xl">
                <h3 className="text-lg font-semibold text-emerald-800 mb-3 flex items-center">
                  <Leaf className="w-5 h-5 mr-2" />
                  Health Benefits
                </h3>
                <ul className="space-y-2">
                  {tea.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-emerald-700">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Helps With:</h3>
              <div className="flex flex-wrap gap-2">
                {tea.ailments.map(ailment => (
                  <span key={ailment} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                    {ailment}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Ingredients:</h3>
              <p className="text-gray-700">{tea.ingredients.join(', ')}</p>
            </div>

            {tea.brewing_instructions && (
              <div className="bg-amber-50 p-4 rounded-xl">
                <h3 className="text-lg font-semibold text-amber-800 mb-2">☕ Brewing Instructions:</h3>
                <p className="text-amber-700 text-sm">{tea.brewing_instructions}</p>
              </div>
            )}

            <Button
              size="lg"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6 rounded-full"
              onClick={addToCart}
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="mr-2 w-5 h-5" />
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
